import React, { useRef, useState, useEffect } from "react";
import RegionManagement from "../components/RegionManagement";
import PageImageManagement from "../components/PageImageManagement";
import { Tabs, Card, Row, Col, Statistic, Table, Button, Tag, Modal, Form, Input, InputNumber, Select, message, Popconfirm, Space } from "antd";
import { AuditOutlined, CrownOutlined, UserOutlined, EnvironmentOutlined, HomeOutlined, PictureOutlined, PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { createMembershipProduct, getAdminMembershipProducts, getAdminUpgradeRequests, getAdminUsers, reviewUpgradeRequest, updateMembershipProduct, updateMembershipProductStatus, updateUserStatus, updateUserRole, getAdminStatistics } from "../services/api";
import { PageLoadingOverlay } from "../components/RoomLoadingScene";

const adminDashboardCache = new Map();

const AdminDashboard = () => {
    const adminId = JSON.parse(localStorage.getItem("user") || "{}").id || "admin";
    const cachedAdminData = adminDashboardCache.get(adminId);
    const roleOptions = [
        { label: "普通用户", value: "user" },
        { label: "房东", value: "landlord" },
        { label: "中介", value: "agent" }
    ];
    const [stats, setStats] = useState(cachedAdminData?.stats || null);
    const [users, setUsers] = useState(cachedAdminData?.users || []);
    const [listedRooms, setListedRooms] = useState(cachedAdminData?.listedRooms || []);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(!cachedAdminData);
    const [userModal, setUserModal] = useState(false);
    const [creatingUser, setCreatingUser] = useState(false);
    const creatingUserRef = useRef(false);
    const [unlistModal, setUnlistModal] = useState(false);
    const [unlistReason, setUnlistReason] = useState("");
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [upgradeRequests, setUpgradeRequests] = useState([]);
    const [membershipProducts, setMembershipProducts] = useState([]);
    const [productModal, setProductModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [savingProduct, setSavingProduct] = useState(false);
    const [reviewModal, setReviewModal] = useState(false);
    const [reviewingRequest, setReviewingRequest] = useState(null);
    const [reviewAction, setReviewAction] = useState("approved");
    const [reviewNote, setReviewNote] = useState("");
    const [form] = Form.useForm();
    const [productForm] = Form.useForm();

    useEffect(() => {
        const quiet = Boolean(cachedAdminData);
        Promise.all([loadStats(), loadUsers(quiet), loadListedRooms(quiet), loadUpgradeRequests(), loadMembershipProducts()]).finally(() => setInitialLoading(false));
    }, []);


    const loadStats = async () => { try { const res = await getAdminStatistics(); setStats(res); adminDashboardCache.set(adminId, { ...(adminDashboardCache.get(adminId) || {}), stats: res }); } catch (e) {} };
    const loadUsers = async (quiet = false) => { try { if (!quiet) setLoading(true); const res = await getAdminUsers(); const nextUsers = Array.isArray(res) ? res : []; setUsers(nextUsers); adminDashboardCache.set(adminId, { ...(adminDashboardCache.get(adminId) || {}), users: nextUsers }); } catch (e) {} finally { if (!quiet) setLoading(false); } };
    const loadUpgradeRequests = async () => { try { const res = await getAdminUpgradeRequests(); setUpgradeRequests(Array.isArray(res) ? res : []); } catch (e) {} };
    const loadMembershipProducts = async () => { try { const res = await getAdminMembershipProducts(); setMembershipProducts(Array.isArray(res) ? res : []); } catch (e) {} };
    
    const loadListedRooms = async (quiet = false) => {
        try {
            if (!quiet) setLoading(true);
            const token = localStorage.getItem("token");
            const res = await fetch("/api/admin/rooms/listed", { headers: { Authorization: "Bearer " + token } });
            const data = await res.json();
            const nextRooms = Array.isArray(data) ? data : [];
            setListedRooms(nextRooms);
            adminDashboardCache.set(adminId, { ...(adminDashboardCache.get(adminId) || {}), listedRooms: nextRooms });
        } catch (e) {} finally { if (!quiet) setLoading(false); }
    };

    const handleToggleStatus = async (id, status) => {
        try { await updateUserStatus(id, status === "active" ? "banned" : "active"); message.success("操作成功"); loadUsers(); } catch (e) { message.error("操作失败"); }
    };

    const handleUpgradeRole = async (userId, newRole) => {
        try {
            await updateUserRole(userId, newRole);
            message.success("角色已更新"); 
            loadUsers(); 
            loadStats();
        } catch (e) { message.error("操作失败"); }
    };

    const handleCreateUser = async () => {
        if (creatingUserRef.current) return;
        creatingUserRef.current = true;
        setCreatingUser(true);
        try {
            const values = await form.validateFields();
            const token = localStorage.getItem("token");
            const res = await fetch("/api/admin/users", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                body: JSON.stringify(values)
            });
            const data = await res.json();
            if (res.ok) { message.success("账号创建成功，默认密码：" + values.password); setUserModal(false); form.resetFields(); loadUsers(); loadStats(); } else { message.error(data.error || "创建失败"); }
        } catch (e) {
            if (!e?.errorFields) message.error("创建失败");
        } finally {
            creatingUserRef.current = false;
            setCreatingUser(false);
        }
    };

    const handleUnlistRoom = (roomId) => {
        setSelectedRoomId(roomId);
        setUnlistReason("房源不合规管理员下架");
        setUnlistModal(true);
    };

    const confirmUnlist = async () => {
        try {
            const token = localStorage.getItem("token");
            await fetch("/api/admin/rooms/" + selectedRoomId + "/unlist", {
                method: "PUT",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                body: JSON.stringify({ reason: unlistReason })
            });
            message.success("房源已下架");
            setUnlistModal(false);
            loadListedRooms();
            loadStats();
        } catch (e) { message.error("操作失败"); }
    };

    const openProductModal = product => {
        setEditingProduct(product || null);
        productForm.setFieldsValue(product ? { ...product, features_text: (product.features || []).join("\n") } : { target_role: "landlord", price: 0, duration_days: 365, status: "unlisted", sort_order: 0, features_text: "" });
        setProductModal(true);
    };

    const saveProduct = async () => {
        try {
            const values = await productForm.validateFields();
            setSavingProduct(true);
            const payload = { ...values, features: String(values.features_text || "").split(/\r?\n/).map(item => item.trim()).filter(Boolean) };
            delete payload.features_text;
            if (editingProduct) await updateMembershipProduct(editingProduct.id, payload); else await createMembershipProduct(payload);
            message.success(editingProduct ? "会员商品已更新" : "会员商品已发布");
            setProductModal(false);
            await loadMembershipProducts();
        } catch (error) { if (!error?.errorFields) message.error(error?.error || "保存失败"); }
        finally { setSavingProduct(false); }
    };

    const toggleProductStatus = async product => {
        try {
            await updateMembershipProductStatus(product.id, product.status === "listed" ? "unlisted" : "listed");
            message.success(product.status === "listed" ? "商品已下架" : "商品已上架");
            loadMembershipProducts();
        } catch (error) { message.error(error?.error || "操作失败"); }
    };

    const openReview = (request, action) => { setReviewingRequest(request); setReviewAction(action); setReviewNote(""); setReviewModal(true); };
    const confirmReview = async () => {
        try {
            await reviewUpgradeRequest(reviewingRequest.id, { action: reviewAction, admin_note: reviewNote });
            message.success(reviewAction === "approved" ? "已通过升级申请" : "已拒绝升级申请");
            setReviewModal(false);
            await Promise.all([loadUpgradeRequests(), loadUsers(), loadStats()]);
        } catch (error) { message.error(error?.error || "审核失败"); }
    };

    const mainUsers = (users || []).filter(u => u.role !== "sub_account");
    
    const getSubAccountCount = (parentId) => {
        return (users || []).filter(u => u.role === "sub_account" && u.parent_id === parentId).length;
    };

    const userColumns = [
        { title: "序号", key: "index", width: 60, render: (_, __, index) => index + 1 },
        { title: "用户名", dataIndex: "username", key: "username", render: (name, record) => (
            <div>
                <span>{name}</span>
                {record.role === "landlord" && getSubAccountCount(record.id) > 0 && (
                    <Tag color="blue" style={{ marginLeft: 8 }}><TeamOutlined /> {getSubAccountCount(record.id)}个子账号</Tag>
                )}
            </div>
        )},
        { title: "手机号", dataIndex: "phone", key: "phone" },
        { title: "角色", dataIndex: "role", key: "role", render: (r, record) => r === "admin"
            ? <Tag color="red">管理员</Tag>
            : <Select size="small" value={r} options={roleOptions} style={{ width: 110 }} onChange={value => handleUpgradeRole(record.id, value)} /> },
        { title: "状态", dataIndex: "status", key: "status", render: (s) => <Tag color={s === "active" ? "green" : "red"}>{s === "active" ? "正常" : "封禁"}</Tag> },
        { title: "操作", key: "action", render: (_, r) => {
            if (r.role === "admin") return null;
            return (
                <div style={{ display: "flex", gap: 8 }}>
                    <Popconfirm title="确定操作?" onConfirm={() => handleToggleStatus(r.id, r.status)}>
                        <Button size="small" type={r.status === "active" ? "danger" : "primary"}>{r.status === "active" ? "封禁" : "解封"}</Button>
                    </Popconfirm>
                </div>
            );
        }}
    ];

    const roomColumns = [
        { title: "序号", key: "index", width: 60, render: (_, __, index) => index + 1 },
        { title: "楼栋", dataIndex: "building_name", key: "building_name" },
        { title: "房号", dataIndex: "room_number", key: "room_number" },
        { title: "户型", key: "layout", render: (_, r) => r.bedrooms + "室" + (r.living_rooms || 0) + "厅" },
        { title: "租金", dataIndex: "rent", key: "rent", render: (v) => "¥" + v + "/月" },
        { title: "区域", dataIndex: "region_name", key: "region_name" },
        { title: "街道", dataIndex: "street_name", key: "street_name" },
        { title: "房东", dataIndex: "landlord_name", key: "landlord_name" },
        { title: "操作人", dataIndex: "listed_by_name", key: "listed_by_name" },
        { title: "状态", dataIndex: "status", key: "status", render: (s) => <Tag color={s === "vacant" ? "green" : "red"}>{s === "vacant" ? "空置" : "已租"}</Tag> },
        { title: "操作", key: "action", render: (_, r) => <Popconfirm title="确定要下架该房源吗？" onConfirm={() => handleUnlistRoom(r.id)}><Button size="small" danger>下架</Button></Popconfirm> }
    ];

    const upgradeColumns = [
        { title: "申请用户", dataIndex: "username", key: "username", render: (value, record) => <div><div>{value}</div><small>{record.phone || "未填写手机号"}</small></div> },
        { title: "申请身份", dataIndex: "target_role", key: "target_role", render: value => <Tag color={value === "landlord" ? "blue" : "purple"}>{value === "landlord" ? "房东" : "中介"}</Tag> },
        { title: "会员方案", key: "product", render: (_, record) => <div><div>{record.product_name}</div><small>¥{Number(record.product_price || 0).toFixed(2)}</small></div> },
        { title: "申请说明", dataIndex: "note", key: "note", render: value => value || "-" },
        { title: "申请时间", dataIndex: "created_at", key: "created_at", render: value => value ? new Date(value).toLocaleString() : "-" },
        { title: "状态", dataIndex: "status", key: "status", render: value => <Tag color={value === "pending" ? "gold" : value === "approved" ? "green" : "red"}>{value === "pending" ? "待审核" : value === "approved" ? "已通过" : "已拒绝"}</Tag> },
        { title: "操作", key: "action", render: (_, record) => record.status === "pending" ? <Space><Button type="primary" size="small" onClick={() => openReview(record, "approved")}>通过</Button><Button danger size="small" onClick={() => openReview(record, "rejected")}>拒绝</Button></Space> : <span>{record.admin_note || "已处理"}</span> }
    ];

    const productColumns = [
        { title: "商品名称", dataIndex: "name", key: "name" },
        { title: "会员身份", dataIndex: "target_role", key: "target_role", render: value => <Tag color={value === "landlord" ? "blue" : "purple"}>{value === "landlord" ? "房东" : "中介"}</Tag> },
        { title: "价格", dataIndex: "price", key: "price", render: value => `¥${Number(value || 0).toFixed(2)}` },
        { title: "有效期", dataIndex: "duration_days", key: "duration_days", render: value => Number(value) > 0 ? `${value}天` : "长期" },
        { title: "状态", dataIndex: "status", key: "status", render: value => <Tag color={value === "listed" ? "green" : "default"}>{value === "listed" ? "已上架" : "未上架"}</Tag> },
        { title: "操作", key: "action", render: (_, record) => <Space><Button size="small" onClick={() => openProductModal(record)}>编辑</Button><Popconfirm title={record.status === "listed" ? "确定下架该会员商品？" : "确定上架该会员商品？"} onConfirm={() => toggleProductStatus(record)}><Button size="small" type={record.status === "listed" ? "default" : "primary"}>{record.status === "listed" ? "下架" : "上架"}</Button></Popconfirm></Space> }
    ];


    const tabItems = [
        {
            key: "users",
            label: <span><UserOutlined /> 用户管理</span>,
            children: (
                <div>
                    <div style={{ marginBottom: 16 }}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setUserModal(true); }}>创建账号</Button>
                    </div>
                    <Table columns={userColumns} dataSource={mainUsers} rowKey="id" loading={loading} />
                </div>
            )
        },
        {
            key: "rooms",
            label: <span><HomeOutlined /> 房源管理</span>,
            children: (
                <div>
                    <h3>已上架房源管理</h3>
                    <Table columns={roomColumns} dataSource={listedRooms} rowKey="id" loading={loading} />
                </div>
            )
        },
        {
            key: "upgrade-requests",
            label: <span><AuditOutlined /> 身份审核{upgradeRequests.some(item => item.status === "pending") ? <Tag color="red" style={{ marginLeft: 6 }}>{upgradeRequests.filter(item => item.status === "pending").length}</Tag> : null}</span>,
            children: <Table columns={upgradeColumns} dataSource={upgradeRequests} rowKey="id" scroll={{ x: 900 }} />
        },
        {
            key: "membership-products",
            label: <span><CrownOutlined /> 会员商品</span>,
            children: <div><Button type="primary" icon={<PlusOutlined />} style={{ marginBottom: 16 }} onClick={() => openProductModal(null)}>发布会员商品</Button><Table columns={productColumns} dataSource={membershipProducts} rowKey="id" scroll={{ x: 760 }} /></div>
        },
        {
            key: "regions",
            label: <span><EnvironmentOutlined /> 区域管理</span>,
            children: <RegionManagement />
        },
        {
            key: "page-images",
            label: <span><PictureOutlined /> 页面图片</span>,
            children: <PageImageManagement />
        }
    ];

    return (
        <div>
            {initialLoading && <PageLoadingOverlay caption="正在加载管理数据，请稍候…" />}
            <div style={{ marginBottom: 24 }}>
                <Row gutter={[16, 16]}>
                    <Col xs={12} lg={6}><Card><Statistic title="总用户数" value={stats?.users?.total || 0} /></Card></Col>
                    <Col xs={12} lg={6}><Card><Statistic title="房东数量" value={stats?.users?.landlords || 0} valueStyle={{ color: "#1890ff" }} /></Card></Col>
                    <Col xs={12} lg={6}><Card><Statistic title="总房源数" value={stats?.rooms?.total || 0} /></Card></Col>
                    <Col xs={12} lg={6}><Card><Statistic title="已上架" value={stats?.rooms?.listed || 0} valueStyle={{ color: "#52c41a" }} /></Card></Col>
                </Row>
            </div>
            <Tabs items={tabItems} />
            <Modal title="创建账号" open={userModal} onOk={handleCreateUser} onCancel={() => { if (!creatingUser) setUserModal(false); }} confirmLoading={creatingUser} cancelButtonProps={{ disabled: creatingUser }}>
                <Form form={form} layout="vertical">
                    <Form.Item name="username" label="用户名" rules={[{ required: true, message: "请输入用户名" }]}><Input placeholder="例如：landlord2" /></Form.Item>
                    <Form.Item name="password" label="密码" rules={[{ required: true, message: "请输入密码" }, { min: 6, message: "密码至少6位" }]}><Input.Password placeholder="至少6位" /></Form.Item>
                    <Form.Item name="phone" label="手机号"><Input placeholder="选填" /></Form.Item>
                    <Form.Item name="role" label="角色" rules={[{ required: true, message: "请选择角色" }]}>
                        <Select placeholder="请选择角色">
                            <Select.Option value="user">普通用户</Select.Option>
                            <Select.Option value="landlord">房东</Select.Option>
                            <Select.Option value="agent">中介</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
            <Modal title="下架房源" open={unlistModal} onOk={confirmUnlist} onCancel={() => setUnlistModal(false)} okText="确认下架" okButtonProps={{ danger: true }}>
                <Form layout="vertical">
                    <Form.Item label="下架原因">
                        <Input.TextArea rows={4} value={unlistReason} onChange={(e) => setUnlistReason(e.target.value)} />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal title={editingProduct ? "编辑会员商品" : "发布会员商品"} open={productModal} onOk={saveProduct} onCancel={() => setProductModal(false)} confirmLoading={savingProduct} okText="保存商品" width={620}>
                <Form form={productForm} layout="vertical">
                    <Row gutter={12}>
                        <Col span={14}><Form.Item name="name" label="商品名称" rules={[{ required: true, message: "请输入商品名称" }]}><Input maxLength={60} placeholder="例如：房东年度会员" /></Form.Item></Col>
                        <Col span={10}><Form.Item name="target_role" label="会员身份" rules={[{ required: true }]}><Select options={[{ label: "房东", value: "landlord" }, { label: "中介", value: "agent" }]} /></Form.Item></Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={8}><Form.Item name="price" label="价格（元）" rules={[{ required: true }]}><InputNumber min={0} precision={2} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={8}><Form.Item name="duration_days" label="开通后的有效天数" extra="从管理员审核通过当天起算，填0为长期" rules={[{ required: true }]}><InputNumber min={0} precision={0} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={8}><Form.Item name="status" label="发布状态"><Select options={[{ label: "暂不上架", value: "unlisted" }, { label: "立即上架", value: "listed" }]} /></Form.Item></Col>
                    </Row>
                    <Form.Item name="description" label="商品说明"><Input.TextArea rows={2} maxLength={500} showCount /></Form.Item>
                    <Form.Item name="features_text" label="会员功能（每行一项）"><Input.TextArea rows={5} placeholder={'发布房源\n管理房源\n查看数据统计'} /></Form.Item>
                    <Form.Item name="sort_order" label="排序值（越小越靠前）"><InputNumber precision={0} /></Form.Item>
                </Form>
            </Modal>
            <Modal title={reviewAction === "approved" ? "通过升级申请" : "拒绝升级申请"} open={reviewModal} onOk={confirmReview} onCancel={() => setReviewModal(false)} okText={reviewAction === "approved" ? "确认通过" : "确认拒绝"} okButtonProps={{ danger: reviewAction === "rejected" }}>
                <p>申请用户：{reviewingRequest?.username || "-"}</p>
                <p>目标身份：{reviewingRequest?.target_role === "landlord" ? "房东" : "中介"}</p>
                <Form layout="vertical"><Form.Item label="管理员说明（选填）"><Input.TextArea rows={4} maxLength={500} value={reviewNote} onChange={event => setReviewNote(event.target.value)} placeholder={reviewAction === "approved" ? "可填写生效说明" : "建议填写拒绝原因"} /></Form.Item></Form>
            </Modal>
        </div>
    );
};

export default AdminDashboard;





